import type { Taxonomy, LearnerProfile } from "./types";

export const CCM_TAXONOMY: Taxonomy = {
  customerId: "ccm-eleva-cl",
  name: "Marco de Cualificaciones para la Minería — CCM-Eleva",
  version: "2024",
  source: "https://ccm-eleva.cl/estandares-mineros/buscador/",
  processes: [
    {
      id: "rajo",
      label: "Extracción mina rajo",
      qualifications: [
        {
          id: "oemr",
          label: "Operación de equipos mina rajo",
          level: 2,
          units: [
            { id: "UCL-001", label: "Trabajar con seguridad según procedimiento y normativa vigente" },
            { id: "UCL-002", label: "Operar camión minero de gran tonelaje" },
            { id: "UCL-003", label: "Regar área de trabajo y asistir operaciones" },
            { id: "UCL-004", label: "Operar excavadora" },
            { id: "UCL-005", label: "Operar tractor neumático (motoniveladora)" },
          ],
        },
      ],
    },
    {
      id: "subte",
      label: "Extracción mina subterránea",
      qualifications: [
        {
          id: "oms",
          label: "Operación mina subterránea",
          level: 2,
          units: [
            { id: "UCL-010", label: "Trabajar con seguridad en mina subterránea" },
            { id: "UCL-011", label: "Operar jumbo electrohidráulico" },
            { id: "UCL-012", label: "Operar LHD / scoop tram" },
            { id: "UCL-013", label: "Operar sistemas de harneros y alimentadores" },
            { id: "UCL-014", label: "Muestreo de puntos de extracción" },
          ],
        },
      ],
    },
    {
      id: "mant",
      label: "Mantenimiento",
      qualifications: [
        {
          id: "mmex",
          label: "Mantenimiento mecánico — equipos de extracción",
          level: 3,
          units: [
            { id: "UCL-020", label: "Mantener sistemas de motor y transmisión" },
            { id: "UCL-021", label: "Mantener sistemas de frenos y dirección" },
            { id: "UCL-022", label: "Diagnosticar fallas en equipos móviles" },
          ],
        },
        {
          id: "mele",
          label: "Mantenimiento eléctrico",
          level: 3,
          units: [
            { id: "UCL-030", label: "Trabajar con seguridad eléctrica" },
            { id: "UCL-031", label: "Mantener sistemas eléctricos de equipos" },
          ],
        },
      ],
    },
    {
      id: "proc",
      label: "Procesamiento",
      qualifications: [
        {
          id: "opc",
          label: "Operación planta concentradora",
          level: 2,
          units: [
            { id: "UCL-040", label: "Operar sistemas de molienda" },
            { id: "UCL-041", label: "Operar sistemas de flotación" },
            { id: "UCL-042", label: "Controlar calidad de concentrado" },
          ],
        },
      ],
    },
  ],
};

export const ALL_UNITS = CCM_TAXONOMY.processes.flatMap((p) =>
  p.qualifications.flatMap((q) => q.units)
);

export function unitById(id: string) {
  return ALL_UNITS.find((u) => u.id === id);
}

// ─── Cross-site reconciliation constants ─────────────────────────────────────

/** The 11-UCL subset used for the reconciliation demo. */
export const REC_ALL_UNITS = [
  { id: "UCL-001", label: "Trabajar con seguridad según procedimiento y normativa vigente" },
  { id: "UCL-002", label: "Operar camión minero de gran tonelaje" },
  { id: "UCL-003", label: "Regar área de trabajo — camión aljibe" },
  { id: "UCL-004", label: "Operar excavadora hidráulica" },
  { id: "UCL-005", label: "Operar tractor neumático (motoniveladora)" },
  { id: "UCL-010", label: "Trabajar con seguridad en mina subterránea" },
  { id: "UCL-011", label: "Operar jumbo electrohidráulico" },
  { id: "UCL-012", label: "Operar LHD / scoop tram" },
  { id: "UCL-020", label: "Mantener sistemas de motor y transmisión" },
  { id: "UCL-021", label: "Mantener sistemas de frenos y dirección" },
  { id: "UCL-022", label: "Diagnosticar fallas en equipos móviles" },
];

/** Embedded demo CSVs — one per site. Column names are hardcoded (no detection needed). */
export const REC_DEMO_SITES: Array<{
  name: string;
  csv: string;
  qCol: string; nCol: string; iCol: string; rCol: string;
}> = [
  {
    name: "Mina Collahuasi",
    qCol: "NombreCurso", nCol: "NombreCompleto", iCol: "NumeroEmpleado", rCol: "Cargo",
    csv: `NombreCompleto,NumeroEmpleado,Faena,Cargo,NombreCurso,FechaCompletado,Vencimiento,Nota,Proveedor
Valentina Rojas,EMP-7712,Mina Collahuasi,Operadora Jumbo,Gestión de Riesgos Mina Subterránea,2025-04-10,2027-04-10,91,CCM-Eleva
Valentina Rojas,EMP-7712,Mina Collahuasi,Operadora Jumbo,Introducción al Jumbo Electrohidráulico,2025-05-20,,88,CCM-Eleva
Sofía Herrera,EMP-9910,Mina Collahuasi,Técnica Mantenimiento,Mantención Motor y Sistemas de Transmisión,2024-08-14,,93,Instituto Técnico Minero
Sofía Herrera,EMP-9910,Mina Collahuasi,Técnica Mantenimiento,Mantención Sistemas de Frenos y Dirección,2024-10-01,,90,Instituto Técnico Minero
Isabel Castro,EMP-5566,Mina Collahuasi,Operadora Camión,Trabajar con Seguridad según Normativa DS-132,2025-01-08,2027-01-08,98,CCM-Eleva
Isabel Castro,EMP-5566,Mina Collahuasi,Operadora Camión,Operación de Camión Komatsu 930E,2025-02-20,2027-02-20,95,Distilled Intuition
Isabel Castro,EMP-5566,Mina Collahuasi,Operadora Camión,Operación Camión Aljibe y Control de Polvo,2025-04-01,,92,CCM-Eleva
Pedro Saavedra,EMP-6678,Mina Collahuasi,Operador Equipos Rajo,Trabajar con Seguridad según Normativa DS-132,2024-11-10,2026-11-10,85,CCM-Eleva
Pedro Saavedra,EMP-6678,Mina Collahuasi,Operador Equipos Rajo,Operación Excavadora Hidráulica,2025-01-25,,79,CCM-Eleva
Rosa Infante,EMP-3310,Mina Collahuasi,Operadora LHD,Gestión de Riesgos Mina Subterránea,2024-06-01,2026-06-01,88,CCM-Eleva
Rosa Infante,EMP-3310,Mina Collahuasi,Operadora LHD,Operación LHD Subterráneo Nivel Avanzado,2024-07-15,,91,CCM-Eleva`,
  },
  {
    name: "Esperanza Mine",
    qCol: "TrainingModule", nCol: "FullName", iCol: "StaffID", rCol: "JobTitle",
    csv: `FullName,StaffID,Department,JobTitle,TrainingModule,DateCompleted,ExpiryDate,Score,Provider
James Mitchell,ESM-001,Operations,Haul Truck Operator,Mine Safety Awareness Program,2024-09-15,2026-09-15,88,SafetyFirst Training
James Mitchell,ESM-001,Operations,Haul Truck Operator,Haul Truck Operation Fundamentals,2024-10-20,2026-10-20,92,Distilled Intuition
Sarah Gonzalez,ESM-002,Operations,Equipment Operator,Mine Safety Awareness Program,2025-01-10,2027-01-10,90,SafetyFirst Training
Sarah Gonzalez,ESM-002,Operations,Equipment Operator,Water Suppression Vehicle Operation,2025-02-14,,85,Internal Training
Sarah Gonzalez,ESM-002,Operations,Equipment Operator,Daily Pre-start Vehicle Inspection,2025-03-01,,91,Internal Training
Marco Reyes,ESM-003,Maintenance,Maintenance Technician,Basic Safety Induction,2024-07-20,2026-07-20,82,SafetyFirst Training
Marco Reyes,ESM-003,Maintenance,Maintenance Technician,Engine and Drivetrain Maintenance,2024-08-15,,88,Instituto Técnico
Marco Reyes,ESM-003,Maintenance,Maintenance Technician,Brake System Maintenance and Inspection,2024-09-22,,84,Instituto Técnico
Linda Torres,ESM-004,Operations,Haul Truck Operator,Mine Safety Awareness Program,2023-11-01,2025-11-01,76,SafetyFirst Training
Linda Torres,ESM-004,Operations,Haul Truck Operator,Haul Truck Operation Fundamentals,2025-04-05,2027-04-05,94,Distilled Intuition
Linda Torres,ESM-004,Operations,Haul Truck Operator,Excavator Basic Operation,2025-05-10,,80,Internal Training
Kevin Okonkwo,ESM-005,Operations,Equipment Operator,Mine Safety Awareness Program,2025-02-20,2027-02-20,91,SafetyFirst Training
Kevin Okonkwo,ESM-005,Operations,Equipment Operator,Haul Truck Operation Fundamentals,2025-03-18,2027-03-18,87,Distilled Intuition
Kevin Okonkwo,ESM-005,Operations,Equipment Operator,Water Suppression Vehicle Operation,2025-04-22,,83,Internal Training`,
  },
  {
    name: "Mina Norte",
    qCol: "NombreCompetencia", nCol: "Nombre", iCol: "ID_Empleado", rCol: "Cargo",
    csv: `Nombre,ID_Empleado,Faena,Cargo,NombreCompetencia,FechaLogro,Vencimiento,Evaluador
Marcos Fuentes,EMP-2233,Mina Norte,Operador Equipos Rajo,Seguridad Operacional Nivel 1,2024-07-10,2026-07-10,Consejo Minero
Marcos Fuentes,EMP-2233,Mina Norte,Operador Equipos Rajo,Equipos Pesados Módulo A,2025-01-15,2027-01-15,OTEC Mina Norte
Diego Torres,EMP-8830,Mina Norte,Técnico Mantenimiento,Seguridad Operacional Nivel 1,2024-08-01,2026-08-01,Consejo Minero
Diego Torres,EMP-8830,Mina Norte,Técnico Mantenimiento,Mantención Preventiva Equipos Diésel,2024-09-15,,OTEC Mina Norte
Diego Torres,EMP-8830,Mina Norte,Técnico Mantenimiento,Sistemas de Frenos Nivel Avanzado,2024-11-01,,OTEC Mina Norte
Carmen López,EMP-4401,Mina Norte,Operadora Camión,Seguridad Operacional Nivel 2,2025-02-10,2027-02-10,Consejo Minero
Carmen López,EMP-4401,Mina Norte,Operadora Camión,Equipos Pesados Módulo A,2025-03-20,2027-03-20,OTEC Mina Norte
Carmen López,EMP-4401,Mina Norte,Operadora Camión,Equipos Pesados Módulo B,2025-04-15,,OTEC Mina Norte
Javier Espinoza,EMP-5523,Mina Norte,Operador Camión,Seguridad Operacional Nivel 1,2023-09-01,2025-09-01,Consejo Minero
Javier Espinoza,EMP-5523,Mina Norte,Operador Camión,Equipos Pesados Módulo A,2024-10-10,2026-10-10,OTEC Mina Norte
Ana Bustamante,EMP-6614,Mina Norte,Operadora Equipos,Seguridad Operacional Nivel 1,2025-01-20,2027-01-20,Consejo Minero
Ana Bustamante,EMP-6614,Mina Norte,Operadora Equipos,Equipos Movimiento de Tierra,2025-03-05,,OTEC Mina Norte`,
  },
  {
    name: "Atacama Operations",
    qCol: "Cert_Name", nCol: "Employee_Name", iCol: "Employee_No", rCol: "Job_Title",
    csv: `Employee_Name,Employee_No,Site_Code,Job_Title,Cert_Code,Cert_Name,Date_Achieved,Expiry_Date,Score_Pct,Certifier
Camila Soto,ATK-3345,ATC-NORTH,Equipment Operator,ATK-HSE-001,Basic Safety Induction,2024-05-12,2026-05-12,96,OSH Certifiers Ltd
Camila Soto,ATK-3345,ATC-NORTH,Equipment Operator,ATK-OPS-001,Heavy Haul Vehicle Operation,2024-06-01,2026-06-01,94,Mine Ops Training
Camila Soto,ATK-3345,ATC-NORTH,Equipment Operator,ATK-OPS-002,Water Cart and Dust Suppression,2024-07-20,,89,Mine Ops Training
Camila Soto,ATK-3345,ATC-NORTH,Equipment Operator,ATK-OPS-003,Grading Equipment Operation,2025-02-14,,88,Mine Ops Training
Héctor Morales,ATK-7788,ATC-NORTH,Maintenance Technician,ATK-HSE-001,Basic Safety Induction,2023-05-20,2025-05-20,72,OSH Certifiers Ltd
Héctor Morales,ATK-7788,ATC-NORTH,Maintenance Technician,ATK-MNT-001,Preventive Maintenance Fundamentals,2025-06-15,,81,Mine Ops Training
Rodrigo Alvarado,ATK-8891,ATC-NORTH,Equipment Operator,ATK-HSE-001,Basic Safety Induction,2024-08-05,2026-08-05,77,OSH Certifiers Ltd
Paula Navarro,ATK-4421,ATC-NORTH,Equipment Operator,ATK-HSE-001,Basic Safety Induction,2025-01-15,2027-01-15,91,OSH Certifiers Ltd
Paula Navarro,ATK-4421,ATC-NORTH,Equipment Operator,ATK-OPS-001,Heavy Haul Vehicle Operation,2025-02-20,2027-02-20,88,Mine Ops Training
Paula Navarro,ATK-4421,ATC-NORTH,Equipment Operator,ATK-HSE-002,Advanced Hazard Identification,2025-03-10,2027-03-10,86,OSH Certifiers Ltd
Tom Villanueva,ATK-2210,ATC-NORTH,Equipment Operator,ATK-HSE-001,Basic Safety Induction,2024-12-01,2026-12-01,89,OSH Certifiers Ltd
Tom Villanueva,ATK-2210,ATC-NORTH,Equipment Operator,ATK-OPS-001,Heavy Haul Vehicle Operation,2025-01-10,2027-01-10,91,Mine Ops Training
Tom Villanueva,ATK-2210,ATC-NORTH,Equipment Operator,ATK-MNT-002,Fault Diagnosis Mobile Equipment,2025-02-28,,79,Mine Ops Training`,
  },
];

import type { RecResult } from "./types";

/** Pre-built mock reconciliation results keyed by site index (0–3). */
export const REC_MOCK_REC: Record<number, RecResult[]> = {
  0: [
    { qual: "Gestión de Riesgos Mina Subterránea",         uclId: "UCL-010", confidence: "HIGH",   rationale: "Risk management in underground mine" },
    { qual: "Introducción al Jumbo Electrohidráulico",     uclId: "UCL-011", confidence: "HIGH",   rationale: "Jumbo drill introduction — direct match" },
    { qual: "Mantención Motor y Sistemas de Transmisión",  uclId: "UCL-020", confidence: "HIGH",   rationale: "Motor and transmission maintenance" },
    { qual: "Mantención Sistemas de Frenos y Dirección",   uclId: "UCL-021", confidence: "HIGH",   rationale: "Brake and steering maintenance" },
    { qual: "Operación Camión Aljibe y Control de Polvo",  uclId: "UCL-003", confidence: "HIGH",   rationale: "Water cart operation — direct match" },
    { qual: "Operación Excavadora Hidráulica",             uclId: "UCL-004", confidence: "HIGH",   rationale: "Hydraulic excavator operation" },
    { qual: "Operación LHD Subterráneo Nivel Avanzado",    uclId: "UCL-012", confidence: "HIGH",   rationale: "LHD underground — direct match" },
    { qual: "Operación de Camión Komatsu 930E",            uclId: "UCL-002", confidence: "HIGH",   rationale: "Haul truck operation — specific model" },
    { qual: "Trabajar con Seguridad según Normativa DS-132", uclId: "UCL-001", confidence: "HIGH", rationale: "Safety per DS-132 — direct UCL-001 match" },
  ],
  1: [
    { qual: "Basic Safety Induction",                  uclId: "UCL-001", confidence: "HIGH",   rationale: "Safety induction covers UCL-001 scope" },
    { qual: "Brake System Maintenance and Inspection", uclId: "UCL-021", confidence: "HIGH",   rationale: "Brake systems maintenance — direct match" },
    { qual: "Daily Pre-start Vehicle Inspection",      uclId: "UCL-001", confidence: "MEDIUM", rationale: "Safety practice, not full competency" },
    { qual: "Engine and Drivetrain Maintenance",       uclId: "UCL-020", confidence: "HIGH",   rationale: "Engine/drivetrain = motor y transmisión" },
    { qual: "Excavator Basic Operation",               uclId: "UCL-004", confidence: "MEDIUM", rationale: "Basic level only, not full endorsement" },
    { qual: "Haul Truck Operation Fundamentals",       uclId: "UCL-002", confidence: "HIGH",   rationale: "Haul truck operation — direct English match" },
    { qual: "Mine Safety Awareness Program",           uclId: "UCL-001", confidence: "MEDIUM", rationale: "Awareness programme, not full competency" },
    { qual: "Water Suppression Vehicle Operation",     uclId: "UCL-003", confidence: "HIGH",   rationale: "Water suppression vehicle = camión aljibe" },
  ],
  2: [
    { qual: "Equipos Movimiento de Tierra",            uclId: "UCL-005", confidence: "MEDIUM", rationale: "Earthmoving maps to motoniveladora UCL-005" },
    { qual: "Equipos Pesados Módulo A",                uclId: "UCL-002", confidence: "MEDIUM", rationale: "Module A likely haul truck, ambiguous" },
    { qual: "Equipos Pesados Módulo B",                uclId: "UCL-004", confidence: "MEDIUM", rationale: "Module B could be excavator, needs review" },
    { qual: "Mantención Preventiva Equipos Diésel",    uclId: "UCL-020", confidence: "HIGH",   rationale: "Diesel preventive maintenance — UCL-020" },
    { qual: "Seguridad Operacional Nivel 1",           uclId: "UCL-001", confidence: "HIGH",   rationale: "Operational safety level 1 — UCL-001" },
    { qual: "Seguridad Operacional Nivel 2",           uclId: "UCL-001", confidence: "HIGH",   rationale: "Advanced safety, same UCL higher level" },
    { qual: "Sistemas de Frenos Nivel Avanzado",       uclId: "UCL-021", confidence: "HIGH",   rationale: "Brake systems advanced — UCL-021" },
  ],
  3: [
    { qual: "Advanced Hazard Identification",       uclId: "UCL-001", confidence: "MEDIUM", rationale: "Hazard ID is safety-adjacent, not full UCL" },
    { qual: "Basic Safety Induction",               uclId: "UCL-001", confidence: "HIGH",   rationale: "Safety induction — UCL-001 direct match" },
    { qual: "Fault Diagnosis Mobile Equipment",     uclId: "UCL-022", confidence: "HIGH",   rationale: "Fault diagnosis mobile equipment — exact" },
    { qual: "Grading Equipment Operation",          uclId: "UCL-005", confidence: "MEDIUM", rationale: "Grading = motoniveladora — plausible match" },
    { qual: "Heavy Haul Vehicle Operation",         uclId: "UCL-002", confidence: "HIGH",   rationale: "Heavy haul = camión minero — direct match" },
    { qual: "Preventive Maintenance Fundamentals",  uclId: "UCL-020", confidence: "HIGH",   rationale: "Preventive maintenance = UCL-020 scope" },
    { qual: "Water Cart and Dust Suppression",      uclId: "UCL-003", confidence: "HIGH",   rationale: "Water cart = camión aljibe — exact match" },
  ],
};

export const ROLE_PROFILES: Record<string, string[]> = {
  "Operador camión minero": ["UCL-001", "UCL-002", "UCL-003"],
  "Operador equipos rajo": ["UCL-001", "UCL-002", "UCL-003", "UCL-004", "UCL-005"],
  "Operador mina subterránea": ["UCL-010", "UCL-011", "UCL-012"],
  "Técnico mantenimiento": ["UCL-001", "UCL-020", "UCL-021", "UCL-022"],
};

export const DEMO_COURSE = {
  id: "DI-CMF-001",
  name: "Operación de camión minero — fundamentos",
  modules: [
    "Introducción al camión Komatsu 930E / CAT 793",
    "Pre-operacional: inspección visual y checklists",
    "Sistemas del camión: motor, transmisión, frenos, dirección",
    "Carga y descarga segura en puntos de pala",
    "Conducción en rampa: velocidades y distancias de seguridad",
    "Procedimientos de emergencia y bloqueo LOTO",
    "EPP requerido y normativa DS-132",
  ],
  extractedUnits: [
    {
      unitId: "UCL-002",
      confidence: "HIGH" as const,
      evidence: "Módulos 1, 4, 5 — operación directa del equipo",
    },
    {
      unitId: "UCL-001",
      confidence: "HIGH" as const,
      evidence: "Módulos 2, 6, 7 — DS-132, LOTO, EPP",
    },
    {
      unitId: "UCL-020",
      confidence: "LOW" as const,
      evidence: "Módulo 3 cubre conocimiento del sistema, no mantenimiento activo",
    },
  ],
};

export const SEED_LEARNERS: LearnerProfile[] = [
  {
    // Fully qualified — Operador camión minero (UCL-001, UCL-002, UCL-003 all covered)
    id: "L001",
    name: "Isabel Castro",
    employeeId: "EMP-5566",
    email: "i.castro@collahuasi.cl",
    site: "Mina Collahuasi",
    role: "Operadora Camión",
    source: "excel",
    importedAt: "2025-04-01",
    completions: [
      {
        id: "C001",
        rawCourseName: "Trabajar con Seguridad según Normativa DS-132",
        normalizedCourseName: "Trabajar con seguridad según procedimiento y normativa vigente",
        completionDate: "2025-01-08",
        expiryDate: "2027-01-08",
        issuingBody: "CCM-Eleva",
        score: 98,
        status: "active",
        mappedUnits: [{ unitId: "UCL-001", confidence: "HIGH", method: "course_content" }],
      },
      {
        id: "C002",
        rawCourseName: "Operación de Camión Komatsu 930E",
        normalizedCourseName: "Operar camión minero de gran tonelaje",
        completionDate: "2025-02-20",
        expiryDate: "2027-02-20",
        issuingBody: "Distilled Intuition",
        score: 95,
        status: "active",
        mappedUnits: [{ unitId: "UCL-002", confidence: "HIGH", method: "course_content" }],
      },
      {
        id: "C003",
        rawCourseName: "Operación Camión Aljibe y Control de Polvo",
        normalizedCourseName: "Regar área de trabajo y asistir operaciones",
        completionDate: "2025-04-01",
        expiryDate: null,
        issuingBody: "CCM-Eleva",
        score: 92,
        status: "active",
        mappedUnits: [{ unitId: "UCL-003", confidence: "HIGH", method: "course_content" }],
      },
    ],
  },
  {
    // Partially qualified — Operador camión minero (has UCL-001 and UCL-002, missing UCL-003)
    id: "L002",
    name: "Pedro Saavedra",
    employeeId: "EMP-6678",
    email: "p.saavedra@collahuasi.cl",
    site: "Mina Collahuasi",
    role: "Operador Equipos Rajo",
    source: "excel",
    importedAt: "2025-04-01",
    completions: [
      {
        id: "C004",
        rawCourseName: "Trabajar con Seguridad según Normativa DS-132",
        normalizedCourseName: "Trabajar con seguridad según procedimiento y normativa vigente",
        completionDate: "2024-11-10",
        expiryDate: "2026-11-10",
        issuingBody: "CCM-Eleva",
        score: 85,
        status: "active",
        mappedUnits: [{ unitId: "UCL-001", confidence: "HIGH", method: "course_content" }],
      },
      {
        id: "C005",
        rawCourseName: "Operación Excavadora Hidráulica",
        normalizedCourseName: "Operar excavadora",
        completionDate: "2025-01-25",
        expiryDate: null,
        issuingBody: "CCM-Eleva",
        score: 79,
        status: "active",
        mappedUnits: [{ unitId: "UCL-004", confidence: "MEDIUM", method: "course_content" }],
      },
    ],
  },
  {
    // Minimally qualified — Operador camión minero (safety only, missing UCL-002 and UCL-003)
    id: "L003",
    name: "Carmen López",
    employeeId: "EMP-4401",
    email: "c.lopez@minanorte.cl",
    site: "Mina Norte",
    role: "Operadora Camión",
    source: "excel",
    importedAt: "2025-04-15",
    completions: [
      {
        id: "C006",
        rawCourseName: "Seguridad Operacional Nivel 2",
        normalizedCourseName: "Trabajar con seguridad según procedimiento y normativa vigente",
        completionDate: "2025-02-10",
        expiryDate: "2027-02-10",
        issuingBody: "Consejo Minero",
        score: 90,
        status: "active",
        mappedUnits: [{ unitId: "UCL-001", confidence: "HIGH", method: "llm_inference" }],
      },
    ],
  },
  {
    // Mostly qualified — Técnico mantenimiento (has UCL-001, UCL-020, UCL-021; missing UCL-022)
    id: "L004",
    name: "Diego Torres",
    employeeId: "EMP-8830",
    email: "d.torres@minanorte.cl",
    site: "Mina Norte",
    role: "Técnico Mantenimiento",
    source: "talentlms",
    importedAt: "2025-04-15",
    completions: [
      {
        id: "C007",
        rawCourseName: "Seguridad Operacional Nivel 1",
        normalizedCourseName: "Trabajar con seguridad según procedimiento y normativa vigente",
        completionDate: "2024-08-01",
        expiryDate: "2026-08-01",
        issuingBody: "Consejo Minero",
        score: 88,
        status: "active",
        mappedUnits: [{ unitId: "UCL-001", confidence: "HIGH", method: "llm_inference" }],
      },
      {
        id: "C008",
        rawCourseName: "Mantención Preventiva Equipos Diésel",
        normalizedCourseName: "Mantener sistemas de motor y transmisión",
        completionDate: "2024-09-15",
        expiryDate: null,
        issuingBody: "OTEC Mina Norte",
        score: 91,
        status: "active",
        mappedUnits: [{ unitId: "UCL-020", confidence: "HIGH", method: "course_content" }],
      },
      {
        id: "C009",
        rawCourseName: "Sistemas de Frenos Nivel Avanzado",
        normalizedCourseName: "Mantener sistemas de frenos y dirección",
        completionDate: "2024-11-01",
        expiryDate: null,
        issuingBody: "OTEC Mina Norte",
        score: 87,
        status: "active",
        mappedUnits: [{ unitId: "UCL-021", confidence: "HIGH", method: "course_content" }],
      },
    ],
  },
  {
    // Partially qualified — Técnico mantenimiento (has UCL-020, UCL-021; missing UCL-001 and UCL-022)
    id: "L005",
    name: "Sofía Herrera",
    employeeId: "EMP-9910",
    email: "s.herrera@collahuasi.cl",
    site: "Mina Collahuasi",
    role: "Técnica Mantenimiento",
    source: "excel",
    importedAt: "2025-04-01",
    completions: [
      {
        id: "C010",
        rawCourseName: "Mantención Motor y Sistemas de Transmisión",
        normalizedCourseName: "Mantener sistemas de motor y transmisión",
        completionDate: "2024-08-14",
        expiryDate: null,
        issuingBody: "Instituto Técnico Minero",
        score: 93,
        status: "active",
        mappedUnits: [{ unitId: "UCL-020", confidence: "HIGH", method: "course_content" }],
      },
      {
        id: "C011",
        rawCourseName: "Mantención Sistemas de Frenos y Dirección",
        normalizedCourseName: "Mantener sistemas de frenos y dirección",
        completionDate: "2024-10-01",
        expiryDate: null,
        issuingBody: "Instituto Técnico Minero",
        score: 90,
        status: "active",
        mappedUnits: [{ unitId: "UCL-021", confidence: "HIGH", method: "course_content" }],
      },
    ],
  },
  {
    // Partially qualified — Operador mina subterránea (has UCL-010 and UCL-012; missing UCL-011 jumbo)
    id: "L006",
    name: "Rosa Infante",
    employeeId: "EMP-3310",
    email: "r.infante@collahuasi.cl",
    site: "Mina Collahuasi",
    role: "Operadora LHD",
    source: "excel",
    importedAt: "2025-04-01",
    completions: [
      {
        id: "C012",
        rawCourseName: "Gestión de Riesgos Mina Subterránea",
        normalizedCourseName: "Trabajar con seguridad en mina subterránea",
        completionDate: "2024-06-01",
        expiryDate: "2026-06-01",
        issuingBody: "CCM-Eleva",
        score: 88,
        status: "active",
        mappedUnits: [{ unitId: "UCL-010", confidence: "HIGH", method: "course_content" }],
      },
      {
        id: "C013",
        rawCourseName: "Operación LHD Subterráneo Nivel Avanzado",
        normalizedCourseName: "Operar LHD / scoop tram",
        completionDate: "2024-07-15",
        expiryDate: null,
        issuingBody: "CCM-Eleva",
        score: 91,
        status: "active",
        mappedUnits: [{ unitId: "UCL-012", confidence: "HIGH", method: "course_content" }],
      },
    ],
  },
];

export const SAMPLE_CSV = `FullName,EmployeeNumber,MineSite,JobRole,CourseTitle,DateCompleted,ExpiryDate,FinalScore,Provider
María Flores,EMP-7712,Mina Collahuasi,Operadora Jumbo,Underground Jumbo Operation,2025-01-10,2027-01-10,91,CCM-Eleva
Diego Torres,EMP-8830,Mina Norte,Técnico Eléctrico,Electrical Safety HV,2024-09-05,2025-09-05,78,Mine Safety Board
Paola Vega,EMP-5521,Mina Atacama,Operadora Camión,Haul Truck Level 1,2025-03-20,,88,Distilled Intuition
Andrés Muñoz,EMP-6634,Mina San José,Operador Jumbo,Jumbo Electro-hidráulico Nivel 2,2024-12-01,2026-12-01,95,CCM-Eleva
Sofía Herrera,EMP-9910,Mina Collahuasi,Técnica Mantenimiento,Engine & Drivetrain Fundamentals,2024-08-14,,85,Technical Institute`;
